import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, articleSlugs } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function buildArticleSchema(slug: string, article: typeof articles[string]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.h1,
    description: article.seoDescription,
    keywords: article.seoKeywords,
    url: `https://superbuyspread.click/blog/${slug}`,
    datePublished: '2026-07-01',
    dateModified: '2026-07-13',
    author: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      url: 'https://superbuyspread.click',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://superbuyspread.click/blog/${slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : undefined;

  if (!article || !articleSlugs.includes(slug || '')) {
    return (
      <main>
        <Navbar />
        <div className="pt-32 pb-20 px-6 text-center min-h-screen flex flex-col items-center justify-center bg-background-50">
          <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-accent-100 mb-6">
            <i className="ri-error-warning-line text-3xl text-accent-600"></i>
          </div>
          <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-500 mb-8">The article you are looking for does not exist or may have been moved.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all text-base whitespace-nowrap cursor-pointer"
          >
            Back to Homepage
            <i className="ri-arrow-left-line"></i>
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const articleSchema = buildArticleSchema(slug!, article);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <main>
        <Navbar />

        <article className="pt-24 pb-16 md:pb-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600 truncate max-w-[200px]">{article.h1}</span>
            </nav>

            {/* Header */}
            <header className="mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-primary-100/80 text-primary-700 text-xs font-semibold rounded-full">{tag}</span>
                ))}
                <span className="text-xs text-foreground-400">{article.readingTime}</span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-6">
                {article.h1}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                {article.intro}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> Last Updated: July 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> Reading Time: {article.readingTime}
                </span>
              </div>
            </header>

            {/* Table of Contents */}
            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                Table of Contents
              </h2>
              <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70">
                <ol className="list-decimal list-inside space-y-2 text-sm text-foreground-600">
                  {article.sections.map((section, idx) => (
                    <li key={section.id}>
                      <a href={`#${section.id}`} className="hover:text-primary-500 transition-colors cursor-pointer">{idx + 1}. {section.title}</a>
                    </li>
                  ))}
                </ol>
              </div>
            </section>

            {/* Article Body */}
            <div className="prose-custom">
              {article.sections.map((section) => (
                <section key={section.id} id={section.id} className="mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                    {section.title}
                  </h2>

                  {section.content.filter(Boolean).map((paragraph, pIdx) => (
                    <p key={pIdx} className="text-foreground-600 leading-relaxed mb-4">{paragraph}</p>
                  ))}

                  {section.listItems && section.listItems.length > 0 && (
                    <div className="space-y-3 mb-6">
                      {section.listItems.map((item) => (
                        <div key={item} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                            <i className="ri-check-line text-accent-600 text-sm"></i>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {section.tableData && section.tableData.length > 0 && (
                    <div className="overflow-x-auto rounded-2xl border border-background-200/70 mb-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            {(() => {
                              const firstRow = section.tableData[0];
                              return (
                                <>
                                  <th className="text-left p-4 font-semibold text-foreground-800">Feature</th>
                                  <th className="text-left p-4 font-semibold text-foreground-500">{firstRow.col1}</th>
                                  <th className="text-left p-4 font-semibold text-primary-600">{firstRow.col2}</th>
                                </>
                              );
                            })()}
                          </tr>
                        </thead>
                        <tbody>
                          {section.tableData.map((row, rIdx) => (
                            <tr key={rIdx} className="border-t border-background-200/70">
                              <td className="p-4 font-medium text-foreground-700">{row.feature}</td>
                              <td className="p-4 text-foreground-500">{row.col1}</td>
                              <td className="p-4 text-primary-600 font-medium">{row.col2}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {section.highlightBox && (
                    <div className="bg-accent-50 border border-accent-200/50 rounded-2xl p-6 my-6">
                      <p className="text-sm text-accent-800 font-medium">
                        <i className="ri-lightbulb-line mr-1.5"></i>
                        <strong>Key Takeaway:</strong> {section.highlightBox}
                      </p>
                    </div>
                  )}

                  {section.faqItems && section.faqItems.length > 0 && (
                    <div className="space-y-4">
                      {section.faqItems.map((faq, fIdx) => (
                        <details key={fIdx} className="group bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden">
                          <summary className="p-5 font-semibold text-foreground-800 cursor-pointer flex items-center justify-between text-sm">
                            {faq.q}
                            <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                            <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                          </summary>
                          <div className="px-5 pb-5">
                            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </section>
              ))}

              {/* Category Links Section */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                  Explore All Superbuy Spreadsheet Categories
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  Browse our complete range of 14 shopping categories. Each category contains a curated selection of trending products for smarter discovery.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-accent-600 text-sm"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-700">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </section>

              {/* Related Articles */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                  Read More Superbuy Spreadsheet Guides
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {articleSlugs
                    .filter((s) => s !== slug)
                    .slice(0, 6)
                    .map((s) => (
                      <Link
                        key={s}
                        to={`/blog/${s}`}
                        className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                      >
                        <span className="text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors line-clamp-2">
                          {articles[s].h1}
                        </span>
                        <div className="flex items-center gap-2 mt-2">
                          {articles[s].tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] font-semibold rounded-full">{tag}</span>
                          ))}
                        </div>
                      </Link>
                    ))}
                </div>
              </section>

              {/* Conclusion CTA */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
                  Ready to Start Discovering?
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  {article.conclusion}
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <Link
                    to="/"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all text-base whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                    <i className="ri-arrow-left-line"></i>
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-foreground-900 text-background-50 font-semibold rounded-full hover:bg-foreground-800 transition-all text-base whitespace-nowrap cursor-pointer"
                  >
                    Visit Superbuy Sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </article>

        <Footer />
      </main>
    </>
  );
}